import React, { useState } from "react";
import { Box, Typography, Button, Paper, Stack, alpha } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AmortisationResults from "./AmortisationResults";
import CapitalAllowanceForm from "../../components/CapitalAllowanceForm";

/**
 * Step 3 view: amortisation breakdown + optional Capital Allowance setup
 * (mirrors RepaymentSchedule for regular payments).
 */
const IrregularAmortisationView = ({
  results,
  schedule,
  step1Inputs,
  caTypeOptions,
  caSettings,
  onBack,
  onStep3Submit,
}) => {
  const [showCaForm, setShowCaForm] = useState(false);

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.01),
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant='h6' fontWeight={600} color='primary.main'>
              HP Loan Amortisation
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              Review the repayment breakdown
            </Typography>
          </Box>
          <Button
            onClick={onBack}
            variant='outlined'
            color='secondary'
            size='small'
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 3,
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              flex: showCaForm ? "1 1 50%" : "1 1 100%",
              minWidth: 0,
              transition: "flex 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <AmortisationResults
              results={results}
              onImportClick={() => setShowCaForm(true)}
              showImportButton={!showCaForm}
            />
          </Box>

          {showCaForm && (
            <Box
              sx={{
                flex: "1 1 50%",
                minWidth: 0,
                maxWidth: "50%",
                overflow: "hidden",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.01),
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  height: "100%",
                }}
              >
                <Stack spacing={4}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography
                        variant='h6'
                        fontWeight={600}
                        color='primary.main'
                        gutterBottom
                      >
                        Capital Allowance Setup
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mt: 0.5 }}
                      >
                        Configure capital allowance details for this asset
                      </Typography>
                    </Box>
                    <Button
                      onClick={() => setShowCaForm(false)}
                      variant='outlined'
                      color='secondary'
                      size='small'
                    >
                      Close
                    </Button>
                  </Box>
                  <CapitalAllowanceForm
                    onSubmit={onStep3Submit}
                    caTypeOptions={caTypeOptions}
                    caSettings={caSettings}
                    assetCost={step1Inputs.assetCost}
                    schedule={schedule}
                  />
                </Stack>
              </Paper>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default IrregularAmortisationView;
